import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { InvoicePreview } from '@/components/InvoicePreview';
import { useQuery } from '@tanstack/react-query';
import { getInvoice, getCompany } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import { Loader2, ChevronLeft, Mail, Download, Eye } from 'lucide-react';
import { showNotification } from '@/lib/generalNotifications';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Invoice } from '@/types/firebase';
import { Timestamp } from 'firebase/firestore';

// Helper to format 'YYYY-MM-DD' as local date (copied from Invoices.tsx)
const formatLocalDate = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    // Parse the date string directly to avoid timezone conversion issues
    const [year, month, day] = dateStr.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      // Invalid date
      return '';
    }
    // Create a date object with local timezone components and set to noon to avoid date shifting
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  } catch (error) {
    // Error formatting date
    return '';
  }
};

export default function InvoicePreviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);
  const [retrievalAttempts, setRetrievalAttempts] = useState(0);
  
  // Get invoice ID from query params or session storage
  const [invoiceId, setInvoiceId] = useState<string | null>(() => {
    // First try to get from URL params
    const idFromParams = searchParams.get('id');
    
    if (idFromParams) {
      // Using invoice ID from URL params
      return idFromParams;
    }
    
    // If no ID in params, check session storage
    const idFromSession = sessionStorage.getItem('savedInvoiceId');
    if (idFromSession) {
      // Retrieved invoice ID from session storage
      return idFromSession;
    }
    
    // If still no ID, check local storage as a backup
    const idFromLocal = localStorage.getItem('savedInvoiceId');
    if (idFromLocal) {
      // Retrieved invoice ID from local storage
      // Copy to sessionStorage for consistent behavior
      sessionStorage.setItem('savedInvoiceId', idFromLocal);
      return idFromLocal;
    }
    
    // No invoice ID found in URL params, session storage, or local storage
    return null;
  });
  
  // Redirect to email preview page
  const handleContinueToEmail = () => {
    setIsNavigating(true);
    
    // Store the ID in both session and local storage for redundancy
    if (invoiceId) {
      sessionStorage.setItem('savedInvoiceId', invoiceId);
      localStorage.setItem('savedInvoiceId', invoiceId);
      // Navigating to email page with invoice ID
    }
    
    // Use React Router's navigate function for reliable SPA navigation
    navigate(`/app/invoices/email?id=${invoiceId}`);
  };
  
  // Handle sending thank you email for paid invoices
  const handleSendThankYouEmail = async () => {
    if (!invoice || !invoice.id) return;

    setIsNavigating(true);
    try {
      // Get company settings for email template
      const companyId = invoice.companyId;
      if (companyId) {
        const companyDocRef = doc(db, 'companies', companyId);
        const companyDocSnap = await getDoc(companyDocRef);
        const company = companyDocSnap.data();
        
        if (company && company.emailSettings?.enableThankYouEmail) {
          // Format the payment date
          const paymentDate = formatDate(new Date());
          
          // Prepare the email template with variables replaced
          let emailTemplate = company.emailSettings.paymentThankYou || '';
          let emailSubject = company.emailSettings.paymentThankYouSubject || 'Payment Received - Thank You';
          
          // Replace template variables
          emailTemplate = emailTemplate
            .replace(/{{customerName}}/g, invoice.customerName || '')
            .replace(/{{invoiceNumber}}/g, invoice.invoiceNumber || '')
            .replace(/{{invoiceAmount}}/g, new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(invoice.total || 0))
            .replace(/{{paymentDate}}/g, paymentDate)
            .replace(/{{companyName}}/g, company.name || '');
          
          emailSubject = emailSubject
            .replace(/{{invoiceNumber}}/g, invoice.invoiceNumber || '')
            .replace(/{{companyName}}/g, company.name || '');
          
          // Send the thank you email
          const response = await fetch('/api/email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              invoice,
              template: emailTemplate,
              subject: emailSubject,
              isThankYouEmail: true
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to send thank you email');
          }

          showNotification({
            title: 'Success',
            message: 'Thank you email sent successfully',
            type: 'success'
          });

          // Navigate back to invoices list
          navigate('/app/invoices');
        }
      }
    } catch (error) {
      // Error sending thank you email
      showNotification({
        title: 'Error',
        message: 'Failed to send thank you email',
        type: 'error'
      });
    } finally {
      setIsNavigating(false);
    }
  };
  
  // Go back to edit invoice
  const handleBack = () => {
    sessionStorage.removeItem('savedInvoiceId');
    sessionStorage.removeItem('invoiceCreationSuccess');
    
    // Check if this is a template and if we need to use a different edit route
    const mode = searchParams.get('mode');
    const isTemplate = invoice?.invoiceType === 'recurring';
    
    if (isTemplate && mode === 'edit') {
      // For templates being edited directly, navigate to the template edit page
      navigate(`/app/invoices/new?type=recurring&template=${invoiceId}`);
    } else {
      // For regular invoices, use the regular edit route
      navigate(`/app/invoices/edit/${invoiceId}`);
    }
  };
  
  // Fetch invoice data with support for templates
  const { data: invoice, isLoading: isLoadingInvoice, error: invoiceError } = useQuery({
    queryKey: ['invoice', invoiceId, user?.companyId],
    queryFn: async () => {
      if (!invoiceId) return null;
      
      // First try to get from regular invoices collection
      const regularInvoice = await getInvoice(invoiceId);
      if (regularInvoice) {
        // Found regular invoice
        return regularInvoice;
      }
      
      // If not found, check in the templates collection
      try {
        // Checking templates collection for ID
        const templateRef = doc(db, 'invoiceTemplates', invoiceId);
        const templateSnap = await getDoc(templateRef);
        
        if (templateSnap.exists()) {
          const templateData = templateSnap.data();
          // Found template
          
          // Convert template format to invoice format for compatibility
          // Create a more complete conversion to ensure all required fields are present
          const convertedData = {
            id: templateSnap.id,
            invoiceNumber: templateData.invoiceNumber || `TEMPLATE-${Date.now()}`,
            customerName: templateData.customerName || '',
            customerEmail: templateData.customerEmail || '',
            customerId: templateData.customerDetails?.id || '',
            customerCompanyName: templateData.customerCompanyName || '',
            invoiceType: 'recurring' as 'recurring' | 'standard' | 'installment',
            status: templateData.status || 'active',
            date: templateData.createdAt ? 
              (templateData.createdAt instanceof Timestamp ? 
                templateData.createdAt.toDate().toISOString() : 
                new Date(templateData.createdAt).toISOString()) : 
              new Date().toISOString(),
            dueDate: templateData.dueDate || new Date().toISOString(),
            items: templateData.lineItems || [],
            subtotal: templateData.invoiceDetails?.subtotal || 0,
            taxRate: templateData.invoiceDetails?.taxRate || 0,
            taxAmount: templateData.invoiceDetails?.taxAmount || 0,
            platformFee: templateData.invoiceDetails?.platformFee || 0,
            total: templateData.invoiceDetails?.total || 0,
            notes: templateData.invoiceDetails?.notes || templateData.notes || '',
            footerText: templateData.footerText || '',
            recurringDetails: templateData.recurringDetails || {
              frequency: 'monthly',
              isActive: true
            },
            emailTemplate: templateData.emailTemplate,
            companyId: templateData.companyId || '',
            customerAddress: templateData.customerDetails?.address || '',
            street: templateData.customerDetails?.street || templateData.street || '',
            city: templateData.customerDetails?.city || templateData.city || '',
            state: templateData.customerDetails?.state || templateData.state || '',
            zip: templateData.customerDetails?.zip || templateData.zip || '',
            paymentMethods: templateData.paymentMethods || ['credit_card'],
            activities: [],
            paymentKey: templateData.paymentKey || `pay_${Date.now()}`,
            createdAt: templateData.createdAt ? 
              (templateData.createdAt instanceof Timestamp ? 
                templateData.createdAt.toDate() : 
                new Date(templateData.createdAt)) : 
              new Date(),
            updatedAt: templateData.updatedAt ? 
              (templateData.updatedAt instanceof Timestamp ? 
                templateData.updatedAt.toDate() : 
                new Date(templateData.updatedAt)) : 
              new Date()
          };
          
          // Converted template data
          return convertedData as unknown as Invoice;
        }
        
        // Template not found either
        return null;
      } catch (err) {
        // Error fetching from templates collection
        return null;
      }
    },
    enabled: !!invoiceId && !!user?.companyId,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
  
  // Fetch company settings
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: () => {
      if (!user?.companyId) {
        throw new Error('Company identifier missing');
      }
      return getCompany(user.companyId);
    },
    enabled: !!user?.companyId,
  });
  
  // Try to recover invoice ID from session storage if needed
  useEffect(() => {
    if (!isLoadingInvoice && !invoice && !invoiceId && retrievalAttempts < 3) {
      // First check sessionStorage
      const idFromSession = sessionStorage.getItem('savedInvoiceId');
      
      if (idFromSession) {
        // Found invoice ID in session storage as last chance
        setInvoiceId(idFromSession);
        return;
      }
      
      // If not in sessionStorage, try localStorage as backup
      const idFromLocal = localStorage.getItem('savedInvoiceId');
      
      if (idFromLocal) {
        // Found invoice ID in local storage
        setInvoiceId(idFromLocal);
        // Copy to sessionStorage for consistent behavior
        sessionStorage.setItem('savedInvoiceId', idFromLocal);
        return;
      }
      
      // Increment attempt counter if nothing found
      setRetrievalAttempts(prev => prev + 1);
      // No invoice ID found (attempt)
    }
  }, [invoice, invoiceId, isLoadingInvoice, retrievalAttempts]);
  
  // Redirect if we still can't find the invoice after max attempts
  useEffect(() => {
    if (!isLoadingInvoice && !invoice && retrievalAttempts >= 3) {
      // Max attempts reached, redirecting to invoices list
      showNotification({
        title: "Error",
        message: "Could not find the invoice. Redirecting to invoices list.",
        type: "error"
      });
      navigate('/app/invoices');
    }
  }, [retrievalAttempts, invoice, isLoadingInvoice, navigate]);
  
  // Check for success message
  useEffect(() => {
    // Check both session and local storage for success flag
    const successFromSession = sessionStorage.getItem('invoiceCreationSuccess');
    const successFromLocal = localStorage.getItem('invoiceCreationSuccess');
    
    if (successFromSession || successFromLocal) {
      // Clear the success messages
      sessionStorage.removeItem('invoiceCreationSuccess');
      localStorage.removeItem('invoiceCreationSuccess');
      
      // Successfully loaded invoice preview after creation
      
      showNotification({
        title: "Success",
        message: "Template created successfully and ready for preview.",
        type: "success"
      });
    }
  }, []);
  
  // Clean up session storage when component unmounts or on successful navigation
  useEffect(() => {
    return () => {
      // Only clean up when leaving the page
      sessionStorage.removeItem('savedInvoiceId');
      sessionStorage.removeItem('invoiceCreationSuccess');
      localStorage.removeItem('savedInvoiceId');
      localStorage.removeItem('invoiceCreationSuccess');
      
      // Cleaned up session and local storage on preview page unload
    };
  }, []);
  
  // Add a check to determine if this is a template
  const isTemplate = invoice?.invoiceType === 'recurring';
  
  // Show loading state - also show loading if we don't have the required data for the query yet
  if (isLoadingInvoice || isLoadingCompany || !invoiceId || !user?.companyId) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary/70" />
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Loading invoice preview...</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please wait while we prepare your invoice</p>
        </div>
      </div>
    );
  }
  
  // Show error state
  if (invoiceError) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <Eye className="h-6 w-6 text-red-600 dark:text-red-500" />
          </div>
          <h3 className="mt-4 mb-2 text-xl font-semibold text-red-700 dark:text-red-500">Error Loading Invoice</h3>
          <p className="text-sm text-red-600 dark:text-red-400">
            {invoiceError instanceof Error ? invoiceError.message : "Unable to load the invoice. Please try again."}
          </p>
          <Button 
            variant="secondary" 
            className="mt-6 w-full"
            onClick={() => navigate('/app/invoices')}
          >
            Return to Invoices
          </Button>
        </div>
      </div>
    );
  }
  
  // Show invoice not found state
  if (!invoice) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <Eye className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </div>
          <h3 className="mt-4 mb-2 text-xl font-semibold text-gray-700 dark:text-gray-300">Invoice Not Found</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The invoice you're looking for could not be found. It may have been deleted or you may not have permission to view it.
          </p>
          <Button 
            variant="secondary" 
            className="mt-6 w-full"
            onClick={() => navigate('/app/invoices')}
          >
            Return to Invoices
          </Button>
        </div>
      </div>
    );
  }
 
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                {isTemplate ? "Template Preview" : "Invoice Preview"}
              </h1>
              <div className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded-full uppercase">
                Preview
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isTemplate 
                ? "Review your recurring invoice template" 
                : `Review your invoice before sending it to ${invoice.customerName}`}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="gap-2 items-center border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/50"
              onClick={handleBack}
              disabled={isNavigating}
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Back to Edit</span>
            </Button>
            <Button
              className="gap-2 items-center bg-primary hover:bg-primary/90 text-white"
              onClick={invoice?.status === 'paid' && !isTemplate ? handleSendThankYouEmail : handleContinueToEmail}
              disabled={isNavigating}
            >
              {isNavigating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  <span>
                    {invoice?.status === 'paid' && !isTemplate 
                      ? "Send Thank You Email" 
                      : isTemplate 
                        ? "Continue to Email Setup" 
                        : "Continue to Email"}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice #:</div>
            <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</div>
          </div>
          <Separator orientation="vertical" className="hidden sm:block h-6 my-auto" />
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Date:</div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatLocalDate(invoice.date)}
            </div>
          </div>
          <Separator orientation="vertical" className="hidden sm:block h-6 my-auto" />
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</div>
            <div className={`px-2 py-0.5 text-xs font-semibold rounded capitalize ${
              isTemplate 
                ? (invoice.status as string) === 'inactive' || (invoice.recurringDetails && !invoice.recurringDetails.isActive)
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
            }`}>
              {isTemplate 
                ? (invoice.status as string) === 'inactive' || (invoice.recurringDetails && !invoice.recurringDetails.isActive)
                  ? "Inactive Template"
                  : "Active Template" 
                : invoice.status}
            </div>
          </div>
          <Separator orientation="vertical" className="hidden sm:block h-6 my-auto" />
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Due Date:</div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatLocalDate(invoice.dueDate)}
            </div>
          </div>
          <Separator orientation="vertical" className="hidden sm:block h-6 my-auto" />
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Amount:</div>
            <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}
            </div>
          </div>
          {isTemplate && invoice.recurringDetails?.frequency && (
            <>
              <Separator orientation="vertical" className="hidden sm:block h-6 my-auto" />
              <div className="flex items-center gap-3">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Frequency:</div>
                <div className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded capitalize">
                  {invoice.recurringDetails.frequency}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="p-8 bg-gray-50 dark:bg-gray-800/30">
          {company && invoice && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <InvoicePreview
                invoice={invoice}
                companySettings={company}
                showPreviewWatermark={true}
                isPreviewAndSend={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 
